import { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid, KeenIcon, Modal } from '@/components';
import { useSnackbar } from 'notistack';
import { HarborStatus, Statuses, UserRoles } from '@/constant';
import usePagination from '@/hooks/usePagination';
import { ErrorApiResponse } from '@/types/api.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import {
  HarborListQueryParams,
  HarborResponse,
  IHarborData,
  HarborDeletionStatus,
  HarborStatusType,
} from '@/types/harbor-management.types';
import {
  useDeleteHarborMutation,
  useGetHarborsQuery,
  useGetHarborByIdQuery,
  useUpdateFeaturedHarborMutation,
  useExportHarborsCsvQuery,
} from '@/services/HarborManagementService';
import { useNavigate } from 'react-router-dom';
import { setSelectedHarborId } from '@/store/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { HarborDeletionRequestModal } from './HarborDeletionRequestModal';
import Select, {
  MultiValue,
  components as selectComponents,
} from 'react-select';
import { useGetRegionsQuery } from '@/services/RegionManagementService';
import { useGetCoastalAreaQuery } from '@/services/HarborManagementService';
import { usePermissions } from '@/hooks/usePermissions';
import { PaginatedApiResponse } from '@/types/api.types';
import { formatFinnishCoordinate } from '@/utils';

// Simple option type for react-select
type Option = { value: string; label: string };

// Type guard to distinguish between paginated and non-paginated responses
const isPaginatedHarborResponse = (
  val: unknown
): val is PaginatedApiResponse<HarborResponse[]> => {
  if (!val || typeof val !== 'object') return false;
  return 'data' in (val as Record<string, unknown>);
};

const getHarborStatusLabel = (status: number | string | undefined) => {
  const numericStatus = Number(status);
  switch (numericStatus) {
    case HarborStatusType.ACTIVE:
      return 'Active';
    case HarborStatusType.INACTIVE:
      return 'Inactive';
    case HarborStatusType.DELETED:
      return 'Deleted';
    case HarborStatusType.PENDING_DELETION:
      return 'Pending Deletion';
    case HarborStatusType.UNDER_RENOVATION:
      return 'Under Renovation';
    default:
      return 'Inactive';
  }
};

const HarborListings = () => {
  const dispatch = useDispatch();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [, setModalState] = useState<{
    isOpen: boolean;
    harbor: IHarborData | null;
    mode: 'view' | 'edit' | 'create' | 'details';
  }>({ isOpen: false, harbor: null, mode: 'view' });
  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    harborId: string | null;
  }>({ isOpen: false, harborId: null });
  const [confirmFeaturedState, setConfirmFeaturedState] = useState<{
    isOpen: boolean;
    harborId: string | null;
    featured: boolean;
  }>({ isOpen: false, harborId: null, featured: false });
  const [featuredFilter, setFeaturedFilter] = useState<string>('all');
  const [, setFormData] = useState<Partial<IHarborData>>({
    name: '',
    description: '',
    location: '',
    latitude: undefined,
    longitude: undefined,
    vhf_channel: undefined,
    contact_email: '',
    contact_phone: '',
    country_code: '+1',
    category: 'harbour',
    country_short_code: 'US',
    website_url: '',
    berth_capacity: undefined,
    admin_email: '',
    min_depth: undefined,
    max_depth: undefined,
    depth_unit: 'm',
    facilities: [],
    business_hours: JSON.stringify({
      monday: {},
      tuesday: {},
      wednesday: {},
      thursday: {},
      friday: {},
      saturday: {},
      sunday: {},
    }),
    status: Statuses.ACTIVE,
    logo: null,
    fairway_map: null,
    media: [],
    marina: { id: '', name: '' },
    regionIds: [],
  });
  const [deletionRequestModalState, setDeletionRequestModalState] = useState<{
    isOpen: boolean;
    harborId: string | null;
    harborName: string | null;
  }>({ isOpen: false, harborId: null, harborName: null });

  const [exportParams, setExportParams] = useState<
    | (Omit<HarborListQueryParams, 'page' | 'limit'> & {
      regionIds?: string[];
      coastalAreaNames?: string[];
    })
    | null
  >(null);

  const [, setErrors] = useState<{ [key: string]: string }>({});
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [deleteHarbor] = useDeleteHarborMutation();
  const [updateFeaturedHarbor] = useUpdateFeaturedHarborMutation();
  const [harborIdToFetch, setHarborIdToFetch] = useState<string | null>(null);

  const [selectedRegions, setSelectedRegions] = useState<MultiValue<Option>>(
    []
  );
  const [regionInputFocused, setRegionInputFocused] = useState(false);
  const [regionSearch, setRegionSearch] = useState('');
  const [debouncedRegionSearch, setDebouncedRegionSearch] = useState('');
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedRegionSearch(regionSearch);
    }, 400);
    return () => clearTimeout(handler);
  }, [regionSearch]);
  const [selectedCoastalAreas, setSelectedCoastalAreas] = useState<
    MultiValue<Option>
  >([]);
  const [coastalInputFocused, setCoastalInputFocused] = useState(false);

  const { data: regionsData } = useGetRegionsQuery({
    status: Statuses.ACTIVE,
    limit: 100,
    search: debouncedRegionSearch,
  });

  const { data: coastalAreasData } = useGetCoastalAreaQuery({});

  const regionOptions = useMemo(() => {
    const list = regionsData?.data as unknown;
    if (Array.isArray(list)) {
      return list.map((region: { id: string | number; name: string }) => ({
        value: String(region.id),
        label: String(region.name),
      }));
    }
    return [] as Option[];
  }, [regionsData]);

  const coastalAreaOptions = useMemo(
    () =>
      coastalAreasData?.map((area) => ({
        value: String(area.name),
        label: String(area.label),
      })) || [],
    [coastalAreasData]
  );

  const handleRegionChange = (selectedOptions: MultiValue<Option>) => {
    const normalized: MultiValue<Option> =
      selectedOptions.length > 0
        ? [selectedOptions[selectedOptions.length - 1]]
        : [];
    setSelectedRegions(normalized);
  };

  const handleCoastalAreaChange = (selectedOptions: MultiValue<Option>) => {
    const normalized: MultiValue<Option> =
      selectedOptions.length > 0
        ? [selectedOptions[selectedOptions.length - 1]]
        : [];
    setSelectedCoastalAreas(normalized);
  };

  const { userData } = useSelector((state: RootState) => ({
    userData: state.auth.userData,
    userType: state.auth.userType,
  }));

  // Feature Listing toggle is shown on the listing page only for Admin users.
  // For harbour admins (Marina role), the toggle is on the detail page (gated by capability).
  const isAdminUser = useMemo(() => {
    const roles = userData?.roles || [];
    return roles.includes(UserRoles.ADMIN);
  }, [userData?.roles]);

  const { hasPermission } = usePermissions();

  const readPermission = useMemo(
    () => hasPermission('harbor_management_read'),
    [hasPermission]
  );
  const updatePermission = useMemo(
    () => hasPermission('harbor_management_update'),
    [hasPermission]
  );
  const deletePermission = useMemo(
    () => hasPermission('harbor_management_delete'),
    [hasPermission]
  );
  const deletionRequestPermission = useMemo(
    () => hasPermission('harbor_deletion_request'),
    [hasPermission]
  );
  const exportPermission = useMemo(
    () => hasPermission('harbor_management_export'),
    [hasPermission]
  );

  const queryParams: HarborListQueryParams & {
    regionIds?: string[];
    coastalAreaNames?: string[];
  } = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
  };

  if (featuredFilter !== 'all') {
    queryParams.is_featured = featuredFilter === 'featured';
  }

  const selectedRegion = selectedRegions[0];
  const selectedCoast = selectedCoastalAreas[0];
  if (selectedCoast) {
    queryParams.coastalAreaNames = [selectedCoast.value];
  } else {
    delete queryParams.coastalAreaNames;
  }
  if (selectedRegion) {
    queryParams.regionIds = [selectedRegion.value];
  } else {
    delete queryParams.regionIds;
  }

  const {
    data: harborsResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetHarborsQuery(queryParams);

  // Export CSV API call
  const { data: exportResponse, isLoading: isExportLoading } =
    useExportHarborsCsvQuery(exportParams ?? {}, { skip: !exportParams });

  useEffect(() => {
    if (exportResponse?.fileUrl) {
      const link = document.createElement('a');
      link.href = exportResponse.fileUrl;
      link.download = 'harbors.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setExportParams(null);
    }
  }, [exportResponse]);

  const handleExport = () => {
    const exportQueryParams: Omit<HarborListQueryParams, 'page' | 'limit'> & {
      regionIds?: string[];
      coastalAreaNames?: string[];
    } = {
      search: debouncedValue || undefined,
    };

    if (featuredFilter !== 'all') {
      exportQueryParams.is_featured = featuredFilter === 'featured';
    }

    const selectedRegion = selectedRegions[0];
    const selectedCoast = selectedCoastalAreas[0];
    if (selectedCoast) {
      exportQueryParams.coastalAreaNames = [selectedCoast.value];
    }
    if (selectedRegion) {
      exportQueryParams.regionIds = [selectedRegion.value];
    }

    setExportParams(exportQueryParams);
  };

  const {
    data: harborResponse,
    isSuccess: harborFetchSuccess,
    error: harborFetchError,
    isFetching: harborFetching,
  } = useGetHarborByIdQuery(harborIdToFetch!, {
    skip: !harborIdToFetch,
  });

  // Compute totals for paginated vs non-paginated responses
  useEffect(() => {
    if (Array.isArray(harborsResponse)) {
      const count = harborsResponse.length || 0;
      handleTotalItems(count);
      handleTotalPages(Math.max(1, Math.ceil(count / pagination.pageSize)));
    } else if (isPaginatedHarborResponse(harborsResponse)) {
      handleTotalPages(harborsResponse.totalPages || 1);
      handleTotalItems(harborsResponse.totalItems || 0);
    } else if (
      harborsResponse &&
      typeof harborsResponse === 'object' &&
      'id' in harborsResponse
    ) {
      // Single harbor object (Marina user case)
      handleTotalItems(1);
      handleTotalPages(1);
    }
  }, [
    harborsResponse,
    handleTotalPages,
    handleTotalItems,
    pagination.pageSize,
  ]);

  useEffect(() => {
    if (harborFetchSuccess && harborResponse) {
      const harborData: IHarborData = {
        id: harborResponse.id,
        name: harborResponse.name,
        description: harborResponse.description || '',
        location: harborResponse.location || '',
        latitude: harborResponse.latitude || undefined,
        longitude: harborResponse.longitude || undefined,
        vhf_channel: harborResponse.vhf_channel || undefined,
        contact_email: harborResponse.contact_email || '',
        contact_phone: harborResponse.contact_phone || '',
        country_code: harborResponse.country_code || '+1',
        category: harborResponse.category || 'harbour',
        country_short_code: harborResponse.country_short_code || 'US',
        website_url: harborResponse.website_url || '',
        berth_capacity: harborResponse.berth_capacity || undefined,
        admin_email: harborResponse.admin_email || '',
        min_depth: harborResponse.min_depth || undefined,
        max_depth: harborResponse.max_depth || undefined,
        depth_unit: harborResponse.depth_unit || 'm',
        facilities: harborResponse.facilities || [],
        regionIds:
          // eslint-disable-next-line
          harborResponse.regions?.map((region: any) => region.id) || [],
        business_hours: JSON.stringify(harborResponse.business_hours || {}),
        status: (harborResponse.status ?? HarborStatusType.INACTIVE) as number,
        logo: harborResponse.logo || null,
        fairway_map: harborResponse.fairway_map || null,
        media: [...(harborResponse.media || [])],
        created_at: harborResponse.created_at,
        featured: harborResponse.featured || false,
        marina: {
          id: harborResponse.manager?.id || '',
          name: harborResponse.manager?.name || '',
        },
        marina_id: harborResponse.manager?.id || '',
        harbor_status: harborResponse.harbor_status || HarborStatus.PUBLISH,
        media_captions:
          harborResponse.media_captions &&
            harborResponse.media_captions.length > 0
            ? harborResponse.media_captions
            : harborResponse.image_captions?.map((caption) => ({
              image_index: caption.image_index,
              captions: caption.translations,
            })) || [],
      };
      setFormData(harborData);
      setErrors({});
      setModalState((prev) => ({ ...prev, isOpen: true, harbor: harborData }));
      setHarborIdToFetch(null);
    }
  }, [harborFetchSuccess, harborResponse]);

  useEffect(() => {
    if (harborFetchError) {
      enqueueSnackbar('Failed to fetch harbor details', { variant: 'error' });
      // setHarborIdToFetch(null);
    }
  }, [harborFetchError, enqueueSnackbar]);

  // useEffect(() => {
  //   if (updateSuccess) {
  //     enqueueSnackbar("Harbor updated successfully", { variant: "success" });
  //     handleCloseModal();
  //     refetch();
  //   }
  // }, [updateSuccess, enqueueSnackbar, refetch]);

  const handleToggleFeatured = async () => {
    if (!confirmFeaturedState.harborId) return;
    try {
      await updateFeaturedHarbor({
        harborId: confirmFeaturedState.harborId,
        featured: confirmFeaturedState.featured,
      }).unwrap();
      enqueueSnackbar(
        `Harbor ${confirmFeaturedState.featured ? 'added to' : 'removed from'} featured listings`,
        { variant: 'success' }
      );
      setConfirmFeaturedState({
        isOpen: false,
        harborId: null,
        featured: false,
      });
      refetch();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to update featured status', { variant: 'error' });
      setConfirmFeaturedState({
        isOpen: false,
        harborId: null,
        featured: false,
      });
    }
  };

  const filteredData: IHarborData[] = useMemo(() => {
    let list: HarborResponse[] = [];

    // Handle different response structures
    if (Array.isArray(harborsResponse)) {
      // MyHarborListResponse is an array of HarborResponse
      list = harborsResponse as HarborResponse[];
    } else if (isPaginatedHarborResponse(harborsResponse)) {
      // Regular paginated response with data property
      list = (harborsResponse.data || []) as HarborResponse[];
    } else if (
      harborsResponse &&
      typeof harborsResponse === 'object' &&
      'id' in harborsResponse
    ) {
      // Single harbor object (Marina user case)
      list = [harborsResponse as HarborResponse];
    }

    if (!list || list.length === 0) return [];

    return list.map((harbor: HarborResponse): IHarborData => {
      return {
        id: harbor?.id,
        name: harbor?.name || '',
        description: harbor?.description || '',
        location: harbor?.location || '',
        latitude: harbor?.latitude || undefined,
        longitude: harbor?.longitude || undefined,
        vhf_channel: harbor?.vhf_channel || undefined,
        contact_email: harbor.contact_email || '',
        contact_phone: harbor.contact_phone || '',
        country_code: harbor.country_code || '+1',
        category: harbor.category || 'harbour',
        country_short_code: harbor.country_short_code || 'US',
        website_url: harbor.website_url || '',
        external_url: harbor.external_url || '',
        berth_capacity: harbor.berth_capacity || undefined,
        admin_email: harbor.admin_email || '',
        min_depth: harbor.min_depth || undefined,
        max_depth: harbor.max_depth || undefined,
        depth_unit: harbor.depth_unit || 'm',
        facilities: harbor.facilities || [],
        business_hours: JSON.stringify(harbor.business_hours || {}),
        status: (harbor.status ?? HarborStatusType.INACTIVE) as number,
        logo: harbor.logo || null,
        fairway_map: harbor.fairway_map || null,
        media: [...(harbor.media || [])],
        created_at: harbor.created_at,
        featured: harbor.featured || false,
        marina: {
          id: harbor.manager?.id || '',
          name: harbor.manager?.name || '',
        },
        marina_id: harbor.manager?.id || '',
        harbor_status: harbor.harbor_status || HarborStatus.PUBLISH,
      };
    });
  }, [harborsResponse]);

  const resetFilters = () => {
    if (searchTerm) resetSearch();
    setFeaturedFilter('all');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setSelectedRegions([]);
    setSelectedCoastalAreas([]);
    resetSearch();
  };

  const handleDelete = async () => {
    if (!confirmDeleteState.harborId) return;
    try {
      await deleteHarbor(confirmDeleteState.harborId).unwrap();
      enqueueSnackbar('Harbor deleted successfully', { variant: 'success' });
      setConfirmDeleteState({ isOpen: false, harborId: null });
      refetch();
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse).data?.message || 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setConfirmDeleteState({ isOpen: false, harborId: null });
    }
  };

  const handleHaborsDetails = useCallback(
    async (harborId: string) => {
      navigate(`/harbors/details`);
      dispatch(setSelectedHarborId({ selectedHarborId: harborId }));
    },
    [dispatch, navigate]
  );

  const columns = useMemo(
    () => [
      {
        accessor: (row: IHarborData) => row.name,
        id: 'name',
        header: 'Harbor Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: IHarborData } }) => (
          <div>
            {readPermission ? (
              <button
                onClick={async () => {
                  await handleHaborsDetails(row.original.id as string);
                }}
                className="text-sm font-medium text-gray-900 hover:text-primary-active focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={`View ${row.original.name}`}
              >
                {row.original.name}
              </button>
            ) : (
              <span className="text-sm font-medium text-gray-900">
                {row.original.name}
              </span>
            )}
            {row.original.deletion_status ===
              HarborDeletionStatus.PENDING_DELETION && (
                <span className="badge badge-warning badge-xs mt-1 block">
                  Deletion Pending
                </span>
              )}
            {row.original.deletion_status === HarborDeletionStatus.DELETED && (
              <span className="badge badge-error badge-xs mt-1 block">
                Soft Deleted
              </span>
            )}
          </div>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IHarborData) => row.location,
        id: 'location',
        header: 'Location',
        enableSorting: false,
        cell: ({ row }: { row: { original: IHarborData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.location}
          </span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IHarborData) => row.latitude,
        id: 'latitude',
        header: 'Latitude',
        enableSorting: false,
        cell: ({ row }: { row: { original: IHarborData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.latitude !== undefined &&
              row.original.latitude !== null
              ? formatFinnishCoordinate(row.original.latitude)
              : '-'}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: IHarborData) => row.longitude,
        id: 'longitude',
        header: 'Longitude',
        enableSorting: false,
        cell: ({ row }: { row: { original: IHarborData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.longitude !== undefined &&
              row.original.longitude !== null
              ? formatFinnishCoordinate(row.original.longitude)
              : '-'}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: IHarborData) => row.contact_email,
        id: 'contact_email',
        header: 'Contact Email',
        enableSorting: false,
        cell: ({ row }: { row: { original: IHarborData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.contact_email}
          </span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IHarborData) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IHarborData } }) => (
          <span className="text-gray-800 font-normal">
            {getHarborStatusLabel(row.original.status)}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: IHarborData) => row.harbor_status,
        id: 'publish_status',
        header: 'Publish Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IHarborData } }) => {
          const status = row.original.harbor_status ?? HarborStatus.PUBLISH;
          return (
            <span className="text-gray-800 font-normal">
              {status === HarborStatus.DRAFT ? 'Draft' : 'Published'}
            </span>
          );
        },
        meta: { className: 'min-w-[100px]' },
      },
      ...(isAdminUser
        ? [
          {
            accessor: (row: IHarborData) => row.featured,
            id: 'featured',
            header: 'Feature Listing',
            enableSorting: false,
            cell: ({ row }: { row: { original: IHarborData } }) => {
              const isInactive = row.original.status === Statuses.INACTIVE;
              const isDisabled = isInactive || !updatePermission;

              return (
                <div className="flex items-center">
                  <label
                    className={`relative inline-flex items-center ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <input
                      type="checkbox"
                      checked={row.original.featured}
                      onChange={() => {
                        if (!isDisabled) {
                          setConfirmFeaturedState({
                            isOpen: true,
                            harborId: row.original.id as string,
                            featured: !row.original.featured,
                          });
                        }
                      }}
                      className="sr-only peer cursor-pointer"
                      disabled={isDisabled}
                      aria-label={`Toggle featured status for ${row.original.name}`}
                    />
                    <div
                      className={`w-11 h-6 ${isDisabled ? 'bg-gray-200' : 'bg-gray-200 peer-checked:bg-blue-600'} peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}
                    ></div>
                    <span
                      className={`ml-3 text-sm font-medium ${isDisabled ? 'text-gray-400' : 'text-gray-900'}`}
                    >
                      {row.original.featured ? 'ON' : 'OFF'}
                    </span>
                  </label>
                </div>
              );
            },
            meta: { className: 'min-w-[150px]' },
          },
        ]
        : []),
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({
          row,
        }: {
          row: { original: IHarborData; index: number };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          table: any;
        }) => (
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                await handleHaborsDetails(row.original.id as string);
              }}
              className="btn btn-sm btn-icon btn-light btn-outline"
              aria-label={`View ${row.original.name}`}
            >
              <KeenIcon type="filled" icon="eye" className="text-gray-500" />
            </button>
            {/* Deletion Request Button - Show if user has deletion request permission */}
            {deletionRequestPermission && !isAdminUser && (
              <button
                onClick={() => {
                  setDeletionRequestModalState({
                    isOpen: true,
                    harborId: row.original.id as string,
                    harborName: row.original.name || 'Unknown Harbor',
                  });
                }}
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`Request deletion of ${row.original.name}`}
                title="Request Deletion"
              >
                <KeenIcon
                  type="filled"
                  icon="trash"
                  className="text-orange-500 contrast:text-primary"
                />
              </button>
            )}
            {/* Hard Delete Button - Show if user has direct delete permission but NOT deletion request permission */}
            {isAdminUser && (
              <button
                onClick={() => {
                  setConfirmDeleteState({
                    isOpen: true,
                    harborId: row.original.id as string,
                  });
                }}
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`Delete ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="trash" className="text-red-500" />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [
      handleHaborsDetails,
      readPermission,
      updatePermission,
      deletePermission,
      deletionRequestPermission,
      isAdminUser,
    ]
  );

  return (
    <div className="card card-grid min-w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-harbor"
              aria-label="Search harbors"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500 contrast:text-white"
              />
              <input
                id="search-harbor"
                placeholder="Search harbors"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none contrast:text-white"
                aria-label="Search harbors by name"
              />
            </label>
          </div>
          {isAdminUser && (
            <div className="flex-shrink-0">
              <select
                id="featured-filter"
                value={featuredFilter}
                onChange={(e) => {
                  setFeaturedFilter(e.target.value);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Filter harbors by featured status"
              >
                <option value="all">All</option>
                <option value="featured">Featured</option>
                <option value="non-featured">Non-Featured</option>
              </select>
            </div>
          )}
          {/* Region Filter */}
          <div className="w-full md:w-64">
            <Select<Option, true>
              isMulti
              options={regionOptions}
              value={selectedRegions}
              onChange={handleRegionChange}
              placeholder={
                selectedRegions.length === 0 && !regionInputFocused
                  ? 'Filter by regions'
                  : ''
              }
              className="react-select-container"
              classNamePrefix="react-select"
              isSearchable={true}
              onInputChange={(inputValue) => {
                setRegionSearch(inputValue);
                return inputValue;
              }}
              onFocus={() => setRegionInputFocused(true)}
              onBlur={() => setRegionInputFocused(false)}
              aria-label="Filter by regions"
              inputId="region-filter-select"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: 40,
                  height: 40,
                  maxHeight: 40,
                  overflow: 'hidden',
                }),
                valueContainer: (base) => ({
                  ...base,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minHeight: 40,
                  height: 40,
                  maxHeight: 40,
                  display: 'flex',
                  alignItems: 'center',
                }),
                singleValue: (base) => ({
                  ...base,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 'calc(100% - 24px)',
                }),
                multiValue: (base) => ({
                  ...base,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  minWidth: 0,
                }),
                option: (base) => ({
                  ...base,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  height: 40,
                }),
                clearIndicator: (base) => ({
                  ...base,
                  padding: 4,
                }),
              }}
              components={{
                ...selectComponents,
                ClearIndicator: (props) => {
                  if (!selectedRegions.length) return null;
                  return <selectComponents.ClearIndicator {...props} />;
                },
              }}
              isClearable={false}
            />
          </div>

          {/* Coastal Area Filter */}
          <div className="w-full md:w-64">
            <Select<Option, true>
              isMulti
              options={coastalAreaOptions}
              value={selectedCoastalAreas}
              onChange={handleCoastalAreaChange}
              placeholder={
                selectedCoastalAreas.length === 0 && !coastalInputFocused
                  ? 'Filter by coastal areas'
                  : ''
              }
              className="react-select-container"
              classNamePrefix="react-select"
              onFocus={() => setCoastalInputFocused(true)}
              onBlur={() => setCoastalInputFocused(false)}
              aria-label="Filter by coastal areas"
              inputId="coastal-area-filter-select"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: 40,
                  height: 40,
                  maxHeight: 40,
                  overflow: 'hidden',
                }),
                valueContainer: (base) => ({
                  ...base,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minHeight: 40,
                  height: 40,
                  maxHeight: 40,
                  display: 'flex',
                  alignItems: 'center',
                }),
                singleValue: (base) => ({
                  ...base,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 'calc(100% - 24px)',
                }),
                multiValue: (base) => ({
                  ...base,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  minWidth: 0,
                }),
                option: (base) => ({
                  ...base,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  height: 40,
                }),
                clearIndicator: (base) => ({
                  ...base,
                  padding: 4,
                }),
              }}
              components={{
                ...selectComponents,
                ClearIndicator: (props) => {
                  if (!selectedCoastalAreas.length) return null;
                  return <selectComponents.ClearIndicator {...props} />;
                },
              }}
              isClearable={false}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Export CSV Button */}
          {exportPermission && (
            <button
              className="btn btn-sm btn-primary flex items-center justify-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={handleExport}
              aria-label={
                isExportLoading
                  ? 'Exporting... filtered harbors as CSV'
                  : 'Export CSV filtered harbors'
              }
              title="Export harbors as CSV"
              disabled={isExportLoading}
            >
              {/* <KeenIcon
                type="solid"
                icon="export"
                className="text-white"
                aria-hidden="true"
              /> */}
              {isExportLoading ? 'Exporting...' : 'Export CSV'}
            </button>
          )}
          {(searchTerm ||
            featuredFilter !== 'all' ||
            selectedRegions.length > 0 ||
            selectedCoastalAreas.length > 0) && (
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={resetFilters}
                aria-label="Clear all filters"
                title="Clear all filters"
              >
                <KeenIcon
                  type="solid"
                  icon="arrows-circle"
                  className="text-gray-600"
                />
                Clear Filters
              </button>
            )}
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching || harborFetching ? (
          <div className="text-center py-4" role="status" aria-live="polite">
            Loading harbors...
          </div>
        ) : isError ? (
          <div className="text-center py-4 text-red-500" role="alert">
            Failed to load harbors. Please try again.
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-4 text-gray-500" role="status">
            No harbors are currently assigned. Please check back later for
            available listings.
          </div>
        ) : (
          <DataGrid
            columns={columns}
            data={filteredData}
            rowSelect={false}
            loading={isLoading || isFetching || harborFetching}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems,
              totalPages,
              setPagination,
              info: `{from} - {to} of {count}`,
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            sorting={[{ id: 'name', desc: false }]}
            serverSide={true}
            onFilterReset={resetFilters}
            hasActiveFilters={!!searchTerm || featuredFilter !== 'all'}
            aria-label="Harbor listings table"
          />
        )}
        <ConfirmationDialog
          open={confirmDeleteState.isOpen}
          onClose={() =>
            setConfirmDeleteState({ isOpen: false, harborId: null })
          }
          onConfirm={handleDelete}
          message="Are you sure you want to delete this harbor?"
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="delete-confirmation-title"
          aria-describedby="delete-confirmation-description"
        />
        <ConfirmationDialog
          open={confirmFeaturedState.isOpen}
          onClose={() =>
            setConfirmFeaturedState({
              isOpen: false,
              harborId: null,
              featured: false,
            })
          }
          onConfirm={handleToggleFeatured}
          message={`Are you sure you want to ${confirmFeaturedState.featured ? 'add' : 'remove'} this harbor ${confirmFeaturedState.featured ? 'to' : 'from'} featured listings?`}
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="featured-confirmation-title"
          aria-describedby="featured-confirmation-description"
        />

        {/* Harbor Deletion Request Modal */}
        <Modal
          open={deletionRequestModalState.isOpen}
          onClose={() =>
            setDeletionRequestModalState({
              isOpen: false,
              harborId: null,
              harborName: null,
            })
          }
          title=""
          disableEnforceFocus={false}
        >
          {deletionRequestModalState.harborId &&
            deletionRequestModalState.harborName ? (
            <HarborDeletionRequestModal
              isOpen={deletionRequestModalState.isOpen}
              harborId={deletionRequestModalState.harborId}
              harborName={deletionRequestModalState.harborName}
              onClose={() =>
                setDeletionRequestModalState({
                  isOpen: false,
                  harborId: null,
                  harborName: null,
                })
              }
              onSuccess={() => {
                refetch();
              }}
            />
          ) : (
            <div className="p-4">Loading...</div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export { HarborListings };
